import { ClipboardList, Loader2, MapPin, Phone } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  isCashOnDelivery,
  isOrderReadyForPickup,
  useDeliveryAccept,
  useDeliveryArrive,
  useDeliveryComplete,
  useDeliveryDecline,
  useDeliveryFail,
  useDeliveryStart,
  useMyRiderDeliveries,
  useRiderDeliveryHistory,
} from "@/hooks/use-rider-deliveries";
import { useRiderEvents } from "@/hooks/use-rider-events";
import { useGpsTracker } from "@/lib/rider/gps-tracker";
import { formatPrice } from "@/lib/format-price";

function OrderStatusBadge({ status }: { status: string }) {
  const { t } = useTranslation();
  const labelKey = `rider.orderStatus.${status}`;
  const label = t(labelKey, status);
  const variant = status === "ready" ? "default" : "secondary";
  return <Badge variant={variant}>{label}</Badge>;
}

export default function RiderDeliveryDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const query = useMyRiderDeliveries();
  const historyQuery = useRiderDeliveryHistory();
  useRiderEvents();

  const accept = useDeliveryAccept();
  const decline = useDeliveryDecline();
  const start = useDeliveryStart();
  const arrive = useDeliveryArrive();
  const complete = useDeliveryComplete();
  const fail = useDeliveryFail();

  const entry =
    query.data?.find((d) => d.delivery.id === id) ??
    historyQuery.data?.find((d) => d.delivery.id === id);

  useGpsTracker({
    deliveryId: id ?? "",
    enabled: Boolean(
      id && entry && ["accepted", "en_route", "arrived"].includes(entry.delivery.state),
    ),
  });

  if (query.isPending || historyQuery.isPending) {
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (!entry) {
    return (
      <div className="p-4">
        <p className="text-muted-foreground">
          {t("rider.detail.notFound", "Pedido no encontrado")}
        </p>
        <Button className="mt-2" onClick={() => navigate("/rider")}>
          {t("rider.detail.back", "Volver")}
        </Button>
      </div>
    );
  }

  const { delivery, order } = entry;
  const address = delivery.addressSnapshot;
  const orderReady = isOrderReadyForPickup(entry);
  const cashOnDelivery = isCashOnDelivery(entry);

  const handle = async (
    mutation: typeof accept | typeof decline | typeof start | typeof arrive,
    successKey: string,
  ) => {
    try {
      await mutation.mutateAsync(delivery.id);
      toast.success(t(successKey, "OK"));
      if (mutation === decline) {
        navigate("/rider");
      }
    } catch (error) {
      toast.error((error as Error).message);
    }
  };

  const handleComplete = async (cashCollected: boolean) => {
    try {
      await complete.mutateAsync({ cashCollected, deliveryId: delivery.id });
      toast.success(
        cashCollected
          ? t("rider.delivery.completeSuccess", "OK")
          : t("rider.delivery.completeUncollectedSuccess", "Entrega registrada sin cobro"),
      );
      navigate("/rider");
    } catch (error) {
      toast.error((error as Error).message);
    }
  };

  const handleFail = async () => {
    const reason = window.prompt(t("rider.detail.failReason", "Motivo (opcional)"));
    if (reason === null) {
      return;
    }
    try {
      await fail.mutateAsync({ deliveryId: delivery.id, reason: reason || undefined });
      toast.success(t("rider.delivery.failSuccess", "Marcado como no entregado"));
      navigate("/rider");
    } catch (error) {
      toast.error((error as Error).message);
    }
  };

  const renderActions = () => {
    switch (delivery.state) {
      case "assigned":
        return (
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => handle(decline, "rider.delivery.declineSuccess")}
              disabled={decline.isPending}
            >
              {t("rider.delivery.decline", "Rechazar")}
            </Button>
            <Button
              className="flex-1"
              onClick={() => handle(accept, "rider.delivery.acceptSuccess")}
              disabled={accept.isPending}
            >
              {t("rider.delivery.accept", "Aceptar")}
            </Button>
          </div>
        );
      case "accepted":
        return (
          <div className="space-y-2">
            <Button
              className="w-full"
              size="lg"
              onClick={() => handle(start, "rider.delivery.startSuccess")}
              disabled={start.isPending || !orderReady}
            >
              {t("rider.delivery.start", "Salir a entregar")}
            </Button>
            {!orderReady ? (
              <p className="text-center text-xs text-muted-foreground">
                {t("rider.delivery.startBlocked", "Esperando que el pedido esté listo")}
              </p>
            ) : null}
          </div>
        );
      case "en_route":
        return (
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="flex-1"
              onClick={handleFail}
              disabled={fail.isPending}
            >
              {t("rider.delivery.fail", "No se pudo entregar")}
            </Button>
            <Button
              className="flex-1"
              size="lg"
              onClick={() => handle(arrive, "rider.delivery.arriveSuccess")}
              disabled={arrive.isPending}
            >
              {t("rider.delivery.arrive", "Llegué")}
            </Button>
          </div>
        );
      case "arrived":
        return (
          <div className="space-y-2">
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={handleFail}
                disabled={fail.isPending}
              >
                {t("rider.delivery.fail", "No se pudo entregar")}
              </Button>
              <Button
                className="flex-1"
                size="lg"
                onClick={() => handleComplete(true)}
                disabled={complete.isPending}
              >
                {cashOnDelivery
                  ? t("rider.delivery.completeCashCollected", "Entregado y cobrado")
                  : t("rider.delivery.complete", "Entregado")}
              </Button>
            </div>
            {cashOnDelivery ? (
              <Button
                variant="ghost"
                className="w-full text-muted-foreground"
                onClick={() => handleComplete(false)}
                disabled={complete.isPending}
              >
                {t("rider.delivery.completeCashNotCollected", "Entregado sin cobrar")}
              </Button>
            ) : null}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-3 p-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">
          {t("rider.detail.orderNumber", "Pedido")} #{order.dailyNumber}
        </h1>
        <div className="flex items-center gap-1">
          <OrderStatusBadge status={order.status} />
          <Badge variant="outline">{delivery.state}</Badge>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <ClipboardList className="h-4 w-4" />
            {t("rider.delivery.itemsTitle", "Pedido")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {order.items && order.items.length > 0 ? (
            <ul className="space-y-1 text-sm">
              {order.items.map((item) => (
                <li
                  key={item.id}
                  className="flex items-center justify-between border-b pb-1 last:border-b-0 last:pb-0"
                >
                  <span>{item.name}</span>
                  <span className="text-muted-foreground">× {item.quantity}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">
              {t("rider.delivery.noItems", "Sin productos")}
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">{t("rider.detail.deliverTo", "Entregar a")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-1 text-sm">
          <p className="font-medium">{address?.label ?? "—"}</p>
          <p className="flex items-center gap-1 text-muted-foreground">
            <MapPin className="h-4 w-4" />
            {address?.line1}
            {address?.line2 ? `, ${address.line2}` : ""}
            {address?.city ? ` — ${address.city}` : ""}
          </p>
          {address?.notes ? (
            <p className="text-xs text-muted-foreground">
              {t("rider.detail.notes", "Notas")}: {address.notes}
            </p>
          ) : null}
          {address?.lat && address?.lng ? (
            <a
              className="inline-block text-primary underline-offset-4 hover:underline"
              href={`https://www.google.com/maps/dir/?api=1&destination=${address.lat},${address.lng}`}
              target="_blank"
              rel="noreferrer"
            >
              {t("rider.delivery.openMap", "Abrir mapa")}
            </a>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">{t("rider.detail.customer", "Cliente")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-1 text-sm">
          <p className="font-medium">{order.contactName ?? "—"}</p>
          {order.contactPhone ? (
            <a
              href={`tel:${order.contactPhone}`}
              className="flex items-center gap-1 text-primary underline-offset-4 hover:underline"
            >
              <Phone className="h-4 w-4" />
              {order.contactPhone}
            </a>
          ) : null}
          <p className="text-muted-foreground">
            {t("rider.detail.payment", "Pago")}:{" "}
            {order.paymentMode === "pay_now"
              ? t("rider.detail.paid", "Pagado")
              : t("rider.detail.cod", "Efectivo")}{" "}
            — {formatPrice(order.total, order.currency)}
          </p>
        </CardContent>
      </Card>

      <div className="pt-2">{renderActions()}</div>
    </div>
  );
}
